/**
 * Google Analytics 4 integration.
 *
 * Loads the GA4 script dynamically when a measurement ID is provided via
 * VITE_GA_MEASUREMENT_ID. Does nothing if the variable is absent, so
 * local development runs without analytics by default.
 */

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

let initialized = false;

/**
 * Analytics runs only after the visitor accepts the cookie banner. The banner
 * (CookieConsent) promises analytics is consent-based, so we honor that here.
 */
export function hasAnalyticsConsent(): boolean {
  try {
    return localStorage.getItem('cookieConsent') === 'accepted';
  } catch {
    return false;
  }
}

/**
 * Toggle GA collection via GA's official opt-out flag (window['ga-disable-<ID>']).
 * We disable it on the authenticated app so a compliance customer's in-app
 * activity is never sent to Google, and whenever consent is absent.
 */
export function setAnalyticsEnabled(enabled: boolean): void {
  if (!GA_ID) return;
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = !enabled;
}

export function initAnalytics() {
  if (initialized || !GA_ID || !hasAnalyticsConsent()) return;
  initialized = true;

  // Load gtag.js
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false }); // we track manually
}

export function trackPageView(path: string) {
  if (!GA_ID || !window.gtag) return;
  window.gtag('event', 'page_view', { page_path: path });
}

export function trackEvent(name: string, params?: Record<string, any>) {
  if (!GA_ID || !window.gtag) return;
  window.gtag('event', name, params);
}
